import { ReactComponent as IconDownload } from '@/assets/svgs/download-icon-small.svg';
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as PackageDataIcon } from '@/assets/svgs/package-data-icon.svg';
import { ReactComponent as PackageDetailsIcon } from '@/assets/svgs/package-details-icon.svg';
import { ReactComponent as PackageCommentsIcon } from '@/assets/svgs/package-icon.svg';
import { ReactComponent as ReferenceEditIcon } from '@/assets/svgs/reference-edit.svg';
import Button from "@/components/Button";
import Tabs, { Tab } from "@/components/Tabs";
import { PACKAGE_TYPE } from '@/constants/package-sharing/index.js';
import { ToastContext } from "@/context/toast/index.js";
import useModal from "@/hooks/use-modal/index";
import usePage from '@/hooks/use-page';
import usePackages from '@/hooks/use-packages/index.js';
import { useLocation, useParams } from '@/lib/router-dom/index.js';
import packageApi from "@/services/api/packages.js";
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from 'react-redux';
import ConfirmSendModal from './components/confirm-send-modal.jsx';
import DownloadAllDataModal from './components/download-all-data-modal.jsx';
import AddDataElementModal from './components/package-add-data-element-modal.jsx';
import PackageComments from './components/package-comments.jsx';
import PackageInformation from './components/package-information.jsx';
import VeridaptDataView from './components/package-veridapt-data-view/index.jsx';
import UpdatePackageReferenceNumberModal from "./components/update-package-reference-number-modal.jsx";
import { BADGE_STATUS } from './utils/get-package-status.js';
import usePackageFolders from '@/hooks/use-package-folder/index.js'
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

export const TabTitle = {
	Data: "Data",
	Comment: "Comments",
	Information: "Information",
}

const defaultTabItems = [
	{
		title: TabTitle.Data,
		id: 1,
		label: (
			<div className="package-details__btn">
				<PackageDataIcon />
				<div className="package-details__text" data-testid="data_tab">{TabTitle.Data}</div>
			</div>
		),
		active: false,
	},
	{
		title: TabTitle.Comment,
		id: 2,
		label: (
			<div className="package-details__btn">
				<PackageCommentsIcon />
				<div className="package-details__text" data-testid="comment_tab">{TabTitle.Comment}</div>
			</div>
		),
		active: false,
	},
	{
		title: TabTitle.Information,
		id: 3,
		label: (
			<div className="package-details__btn">
				<PackageDetailsIcon />
				<div className="package-details__text" data-testid="information_tab">{TabTitle.Information}</div>
			</div>
		),
		active: false
	}
];

const Page = () => {

	/*=================================================
		General
	/================================================*/
	const packageActivity = "package";
	const { eclipId } = useParams();
	const user = useSelector(state => state.auth.user);
	const { currentState } = useLocation();
	const { addToastMessage } = useContext(ToastContext);

	const [loading, setLoading] = useState(false);
	const [hasDraft, setHasDraft] = useState(false)

	const [selectedTabLists, setSelectedTabLists] = useState(
		defaultTabItems?.map((item) => ({
			...item,
			active: item.id === 1,
			isVisible: item.id !== 2, // Disabling visibility of comment section as initial
		}))
	);

	const selectTabAction = (item) => {
		const newTabItems = selectedTabLists?.map((value) => {
			value.active = item.id === value.id;
			return value;
		});
		setSelectedTabLists(newTabItems);
	};

	/*=================================================
		Set Folder and Package Data
	/================================================*/
	const {
		currentPackage,
		currentDataElement,
		setCurrentDataElement,
		getPackage,
		placementList,
	} = usePackages()

	const { getFolder, currentFolder } = usePackageFolders()

	function reload() {
		getPackage(eclipId);
	}

	/* A function to check some of external participant is prensent in package */
	const checkExternalParticipantAvailability = (participant) => {

		/* Handling -ve cases */
		if (
			// Validating participant
			!participant?.id ||
			// Skipping for the current loggedIn user organizationID
			participant.id === user?.organizationId
		) {
			return false;
		}

		/* If any external participant present then we returning true */
		return true;
	}

	useEffect(() => {
		reload()
	}, [eclipId])

	/* cDU on logged user and currentPackage */
	useEffect(() => {

		/* Validating present of participant other than logger participant */
		const isExternalParticipantAvailable = (currentPackage?.participants?.some(
			checkExternalParticipantAvailability,
		)) || false;

		/**
		 * If an external participant is there then
		 * we're setting isVisible: true for comment tab
		 **/
		setSelectedTabLists(
			selectedTabLists.map((tabItem) => ({
				...tabItem,
				isVisible: tabItem.id === 2 ? isExternalParticipantAvailable : tabItem.isVisible,
			})),
		);
	}, [user, currentPackage]);

	/*=================================================
		Set Participants Lists
	/================================================*/
	const [participantsList, setParticipantsList] = useState([]);

	/*=================================================
		Modals
	/================================================*/
	const [packageModalType, setPackageModalType] = useState("Update")

	const { visible: isDownloadAllModalVisible, hide: hideDownloadAllModal, show: showDownloadAllModal } = useModal();

	const {
		visible: visibleReferenceModal,
		show: showReferenceModal,
		hide: hideReferenceModal,
	} = useModal();

	const {
		visible: visibleAddDataElementeModal,
		show: showAddDataElementeModal,
		hide: hideAddDataElementeModal,
	} = useModal();

	const {
		visible: isConfirmSendVisible,
		hide: hideConfirmSendModal,
		show: showConfirmSendModal
	} = useModal();


	const handleSubmitAddRefernce = async (referenceNumber) => {
		setLoading(true)
		const [error] = await packageApi.setRefNumber(eclipId, { referenceNumber });
		setLoading(false)
		hideReferenceModal();
		if (error) return addToastMessage("Failed to update Reference ID", 'error')
		getPackage(eclipId)
		return addToastMessage("Reference ID Updated", 'success')
	};

	const handleSendPackage = async () => {
		hideConfirmSendModal()

		setLoading(true)
		const [error] = await packageApi.sendPackage(currentPackage.id);
		setLoading(false)

		if (error) {
			return addToastMessage("Failed to send Package", 'error')
		}
		addToastMessage("Package successfully sent", 'success')
		reload();
	}

	useEffect(() => {
		if (currentPackage) {
			//To set breadcrumb
			if(currentPackage?.eclipFolderId) {
				getFolder(currentPackage?.eclipFolderId)
			}

			if (currentPackage.participants) {
				const participantList = currentPackage.participants?.map((participant) => {
					if (currentPackage.owners?.includes(participant.id)) return { ...participant, isOwner: true }
					else return { ...participant, isOwner: false };
				});
				setParticipantsList(participantList);
			}

			// Format DataElement to add summary tags isXXX [isDownloadable]
			if (currentPackage.dataElements?.length) {
				const updatedDataElements = currentPackage.dataElements.map(item => {
					//const isPayloadAdded = (item?.savedPayload?.length > 0 && Object.keys(item?.savedPayload?.[0]).length > 0) || (item?.savedPayload && Object.keys(item?.savedPayload).length > 0)

					let isDownloadable = false

					if ((item?.messageStoreId || (item?.messageStoreIds && item?.messageStoreIds?.length > 0))) {
						isDownloadable = true;
					}

					return {
						...item,
						isDownloadable
					};
				})

				setCurrentDataElement(updatedDataElements);

				const hasDraftDataElement = currentPackage.dataElements.some(dataElement => dataElement.isDraft)
				hasDraftDataElement ? setHasDraft(true) : setHasDraft(false)

			}
		}
	}, [currentPackage])

	/*=================================================
		Set Breadcrumbs Data
	/================================================*/
	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({
		title: 'Package Sharing',
		breadcrumbs: [],
	});

	useEffect(()=>{
		const updatedBreadcrumbItem = [
			{
				label: '',
				link: '/'
			 },
			{
				label: 'Package Sharing',
				link: '/package'
			 },
			...(currentFolder?.name
				? [
					{
						label: currentFolder?.name,
						link: `/package/folder/${currentFolder?.id}`
					}
				]
				: []),
			{
				label: currentPackage?.name,
				link: ``
			 },
		];

		setBreadcrumbsItems(updatedBreadcrumbItem)
	},[currentFolder,currentPackage])

function openReferenceModal(type) {
		setPackageModalType(type);
		showReferenceModal();
	}

	const renderDownloadButton = () => {
		return currentDataElement?.some(s => s.isDownloadable) ? (
			<Button round ghost prefix={<IconDownload />} text={"Download All"} submit={() => showDownloadAllModal()} />
		) : <></>
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			{/* Start Of Header */}
			<div className='package-details'>

				{/* Heading text */}
				<div className='package-details__heading-tab'>
					<div className='package-details__heading-text'>{currentPackage?.name}</div>

					{(!currentPackage.sent || (currentPackage.sent && hasDraft) || currentPackage.badge === BADGE_STATUS.deliveryFailed) && (
						<Button round
							disabled={!currentPackage?.allowedToSend}
							text={currentPackage?.sent ? "Update Package" : "Send Package"}
							submit={showConfirmSendModal}
						/>
					)}

				</div>

				{/* Package Reference ID */}
				<div className='package-details__reference-tab'>
					<div className='package-details__left-heading'>{"Package Ref ID: "}</div>
					<div>
						<span className='package-details__reference-veridapt'>
							{currentPackage?.type === PACKAGE_TYPE.send && currentPackage?.referenceNumber}
							{currentPackage?.type === PACKAGE_TYPE.receive && currentPackage?.receivedReferenceNumber}
						</span>
						{
							currentPackage?.type === PACKAGE_TYPE.receive && !currentPackage?.referenceNumber &&
							<span className='package-details__reference-reference' onClick={() => openReferenceModal("Add")}>
								{"+ Add my Ref ID"}
							</span>
						}
						{
							currentPackage?.type === PACKAGE_TYPE.send && !currentPackage?.isVeridapt &&
							<span className='package-details__reference-reference' onClick={() => openReferenceModal("Update")}>
								<ReferenceEditIcon />{" Update Ref ID"}
							</span>
						}
					</div>
				</div>

				{/* Your Reference ID */}
				{currentPackage?.type === PACKAGE_TYPE.receive && currentPackage?.referenceNumber &&
					<div className='package-details__reference-tab'>
						<div className='package-details__left-heading'>{"Your Ref ID: "}</div>
						<div>
							<span className='package-details__reference-veridapt'>{currentPackage?.referenceNumber}</span>
							<span className='package-details__reference-reference'
								onClick={() => openReferenceModal("Update")}
							>
								<ReferenceEditIcon />{" Update Ref ID"}
							</span>
						</div>
					</div>
				}

				{/* Participants */}
				<div className='package-details__reference-tab'>
					<div className='package-details__left-heading'>{"Participants: "}</div>
					<div className='package-details__right-data'>
						{participantsList?.length === 0 && (<div>No participant</div>)}
						{participantsList?.slice(0, 3)?.map((participant, index) =>
							<div key={participant.id}>
								{participant.name}
								{participant.isOwner && <span className='package-details__right-data__owner-class'>{"(Owner)"}</span>}
								{participantsList?.length > 3 && (index === 2) && (
									<span className="package-details__participant-count-text tooltip">
										{`... +${participantsList?.length - 3} more`}
										<span className={participantsList?.length > 0 ? "package-details__tooltiptext__package-participant-name" : ''}>
											{participantsList?.slice(0, 5)?.map((participant) => <div key={participant.id}>{participant.name}</div>)}
											{participantsList?.length > 5 && <div className="package-card__participant-count__tooltip-text">{`...+${participantsList.length - 5} more`}</div>}
										</span>
									</span>
								)}
								{/* {!currentPackage?.isVeridapt && (index === 2) && currentPackage.owners?.includes(user.organizationId) && <span className='package-details__add-participants'>{"+ Add Participant"}</span>} */}
							</div>
						)}
					</div>
				</div>

				{/* Start Of tabs */}
				<div className="package-details__tabs-wrapper">
					<div className='package-details__tab-details'>
						<div className='package-details__tabs'>
							<Tabs>
								{selectedTabLists?.map((item, index) => (
									<Tab
										key={item.id}
										label={item.label}
										active={item.active}
										isVisible={item.isVisible}
										onClick={() => selectTabAction(item)}
									/>
								))}
							</Tabs>
						</div>
					</div>
					<div className="package-details__tabs-action-btn">
						<Button round ghost text={"+ Add Data Element"} submit={() => showAddDataElementeModal()} />
						{renderDownloadButton()}
					</div>
				</div>
				{/* End Of tabs */}

			</div>
			{/* End Of Header */}

			{/* Package Tab Selection */}
			<div className="package-detail__contents-wrapper">
				{selectedTabLists[0].active && currentPackage.id && <VeridaptDataView currentPackage={currentPackage} currentFolder={currentFolder} reload={reload} />}
				{selectedTabLists[1].active && <PackageComments packageInfo={currentPackage} />}
				{selectedTabLists[2].active && <PackageInformation currentPackage={currentPackage} />}
			</div>

			{/* Package Reference Modal */}
			<UpdatePackageReferenceNumberModal
				visible={visibleReferenceModal}
				close={hideReferenceModal}
				handleSubmit={handleSubmitAddRefernce}
				type={packageModalType}
				data={currentPackage}
			/>

			<AddDataElementModal
				visible={visibleAddDataElementeModal}
				close={hideAddDataElementeModal}
				currentPackage={currentPackage}
				placementList={placementList}
				reload={reload}
			/>

			<DownloadAllDataModal
				visible={isDownloadAllModalVisible}
				close={hideDownloadAllModal}
				messageStoreId={currentDataElement
					?.filter((item) => item?.isDownloadable && item.messageStoreId)
					?.map(s => s.messageStoreId)
				}
				all={true}
				dataElementId={currentDataElement
					?.filter((item) => item?.isDownloadable && item.messageStoreId)
					?.map(s => s.dataElementId)
				}
				downloadName={currentPackage?.name}
				downloadId={[currentPackage?.id]}
				packageActivity={packageActivity}
			/>

			<ConfirmSendModal
				isVisible={isConfirmSendVisible}
				title={`Send Package Confirmation`}
				text={`Are you sure you want to send ${currentPackage?.name}?`}
				cancel={hideConfirmSendModal}
				confirm={handleSendPackage}
			/>

			{loading && ReactDOM.createPortal(
				<div className="modal white">
					<div className="loading">
						<img src={assetUrl("/loading.gif")} />
					</div>
				</div>,
				document.body
			)}
		</>
	);
};

export default Page;
